import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { type CustomRange } from "@/shared/domain/dashboards/DashboardDefinition";

dayjs.extend(utc);

/**
 * The chip's label for an applied custom range.
 *
 * Doubles as the chip's state: dates mean a custom range is active, so this is
 * what tells the user which window they are on without reopening the picker.
 * A shared month collapses ("Jan 5 – 20") because the chip sits in a header
 * that competes for width.
 *
 * The year is shown unless the window sits entirely in the current one, where
 * it would be noise. Omitting it anywhere else is worse than noise: "Aug 3 – 4"
 * reads as this August, so a window in a previous year would silently look
 * like the wrong data. A window that crosses a year boundary dates both ends;
 * otherwise one trailing year covers both.
 *
 * `now` is injectable so the current year is not ambient state — the same
 * range formats the same way in every test run.
 */
export function formatCustomRange(
  range: CustomRange,
  now: Date = new Date(),
): string {
  const start = dayjs.utc(range.start);
  const end = dayjs.utc(range.end);

  if (start.year() !== end.year()) {
    return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
  }

  // UTC on both sides: the bounds are UTC calendar dates, so comparing them
  // against a local year could disagree with the window the user picked.
  const year =
    start.year() === dayjs.utc(now).year() ? "" : `, ${start.format("YYYY")}`;

  if (start.month() === end.month()) {
    return `${start.format("MMM D")} – ${end.format("D")}${year}`;
  }
  return `${start.format("MMM D")} – ${end.format("MMM D")}${year}`;
}
